import { Suspense } from "react";
import { RouteObject, RouterProvider, createBrowserRouter } from "react-router";
import { routes } from "../routes";
import { MainProgressBarEnum } from "../../enums/mainProgressBarEnum";
import { mapStepToRoute } from "../../../services/routes/progressRouteStepMapper";
import CustomSpinner from "../../../components/spinner/CustomSpinner";
import RootLayout from "../../../pages/layoutes/rootLayout/RootLayout";
import { ProgressBarOutlet } from "../progressBarOutlet";
import { PrivateOutlet } from "../privateOutlet";
import NotFound from "../../../pages/notFound/NotFound";
import AuthLayout from "../../../pages/layoutes/authLayout/AuthLayout";
import { lazyRetry } from "../../../services/system/importComponents";
import { UserRolesEnum } from "../../enums/userRolesEnum";
import Login from "../../../features/auth/Login";
import { AuthLoadingProvider } from "../../../pages/layoutes/authLayout/AuthLoadingProvider";
import UserProfile from "../../../pages/userProfile/profile/UserProfile";
const SignUp = lazyRetry(() => import("../../../features/auth/signUp/SignUp"));
const ConfirmEmail = lazyRetry(() => import("../../../features/auth/ConfirmEmail"));
const ForgotPassword = lazyRetry(() => import("../../../features/auth/ForgotPassword"));
const ResetPassword = lazyRetry(() => import("../../../features/auth/ResetPassword"));
const OrderForm = lazyRetry(() => import("../../../pages/orderPipeline/orderForm/OrderForm"));
const DeliveryOptions = lazyRetry(() => import("../../../pages/orderPipeline/deliveryOptions/DeliveryOptions"));
const RecommendedServices = lazyRetry(
    () => import("../../../pages/orderPipeline/recommendedServices/RecommendedServices")
);
const Verification = lazyRetry(() => import("../../../pages/orderPipeline/verification/Verification"));
const Booking = lazyRetry(() => import("../../../pages/orderPipeline/booking/Booking"));
const ThankYouPage = lazyRetry(() => import("../../../pages/orderPipeline/thankYouPage/ThankYouPage"));
const TariffUploader = lazyRetry(() => import("../../../pages/userProfile/adminPanel/tariffUploader/TariffUploader"));
const MyOrders = lazyRetry(() => import("../../../pages/userProfile/myOrders/MyOrders"));
const MySuggestions = lazyRetry(() => import("../../../pages/userProfile/mySuggestions/MySuggestions"));
const SelectedOrder = lazyRetry(() => import("../../../pages/userProfile/selectedOrder/SelectedOrder"));
const AddressBook = lazyRetry(() => import("../../../pages/userProfile/addressBook/AddressBook"));
const ExchangeRatesPage = lazyRetry(() => import("../../../pages/userProfile/adminPanel/exchangeRates/ExchangeRates"));
const SupportPage = lazyRetry(() => import("../../../pages/support/SupportPage"));
const NotificationsPage = lazyRetry(() => import("../../../pages/notifications/NotificationsPage"));
const AdminPanel = lazyRetry(() => import("../../../pages/userProfile/adminPanel/AdminPanel"));

const mainRoutes = () =>
    [
        {
            element: (
                <Suspense fallback={<CustomSpinner isFullScreen={true} />}>
                    <RootLayout />
                </Suspense>
            ),
            children: [
                {
                    element: <ProgressBarOutlet expectedStep={MainProgressBarEnum.Search} />,
                    children: [
                        {
                            index: true,
                            element: <OrderForm />,
                        },
                    ],
                },
                {
                    element: <ProgressBarOutlet expectedStep={MainProgressBarEnum.DeliveryOptions} />,
                    children: [
                        {
                            path: mapStepToRoute(MainProgressBarEnum.DeliveryOptions),
                            element: <DeliveryOptions />,
                        },
                    ],
                },
                {
                    element: <PrivateOutlet />,
                    children: [
                        {
                            element: <ProgressBarOutlet expectedStep={MainProgressBarEnum.RecommendedServices} />,
                            children: [
                                {
                                    path: mapStepToRoute(MainProgressBarEnum.RecommendedServices),
                                    element: <RecommendedServices />,
                                },
                            ],
                        },
                    ],
                },
                {
                    element: <PrivateOutlet />,
                    children: [
                        {
                            element: <ProgressBarOutlet expectedStep={MainProgressBarEnum.Booking} />,
                            children: [
                                {
                                    path: mapStepToRoute(MainProgressBarEnum.Booking),
                                    element: <Booking />,
                                },
                            ],
                        },
                    ],
                },
                {
                    element: <PrivateOutlet />,
                    children: [
                        {
                            element: <ProgressBarOutlet expectedStep={MainProgressBarEnum.Verification} />,
                            children: [
                                {
                                    path: mapStepToRoute(MainProgressBarEnum.Verification),
                                    element: <Verification />,
                                },
                            ],
                        },
                    ],
                },
                {
                    element: <PrivateOutlet />,
                    children: [
                        {
                            element: <ProgressBarOutlet expectedStep={MainProgressBarEnum.OrderConfirmation} />,
                            children: [
                                {
                                    path: mapStepToRoute(MainProgressBarEnum.OrderConfirmation),
                                    element: <ThankYouPage />,
                                },
                            ],
                        },
                    ],
                },
                {
                    element: <PrivateOutlet />,
                    children: [
                        {
                            path: routes.profile.route,
                            element: <UserProfile />,
                        },
                        {
                            path: routes.myOrders.route,
                            element: <MyOrders />,
                        },
                        {
                            path: routes.mySuggestions.route,
                            element: <MySuggestions />,
                        },
                        {
                            path: `${routes.myOrders.route}/:orderNumber`,
                            element: <SelectedOrder />,
                        },
                        {
                            path: routes.addressBook.route,
                            element: <AddressBook />,
                        },
                        {
                            path: routes.support.route,
                            element: <SupportPage />,
                        },
                        {
                            path: routes.notifications.route,
                            element: <NotificationsPage />,
                        },
                    ],
                },
                {
                    element: <PrivateOutlet roles={[UserRolesEnum.Admin]} />,
                    children: [
                        {
                            path: routes.tariffLoader.route,
                            element: <TariffUploader />,
                        },
                        {
                            path: routes.exchangeRates.route,
                            element: <ExchangeRatesPage />,
                        },
                        {
                            path: routes.adminPanel.route,
                            element: <AdminPanel />,
                        },
                    ],
                },
                {
                    element: (
                        <AuthLoadingProvider>
                            <AuthLayout isShow={true} />
                        </AuthLoadingProvider>
                    ),
                    children: [
                        {
                            path: routes.login.route,
                            element: <Login />,
                        },
                        {
                            element: <PrivateOutlet roles={[UserRolesEnum.Admin]} />,
                            children: [
                                {
                                    path: routes.signUp.route,
                                    element: <SignUp />,
                                },
                            ],
                        },
                        {
                            path: routes.confirmEmail.route,
                            element: <ConfirmEmail />,
                        },
                        {
                            path: routes.forgotPassword.route,
                            element: <ForgotPassword />,
                        },
                        {
                            path: routes.resetPassword.route,
                            element: <ResetPassword />,
                        },
                    ],
                },
                {
                    path: "*",
                    element: <NotFound />,
                },
            ],
        },
    ] as RouteObject[];

export const RouterBuilder = () => {
    const router = createBrowserRouter(mainRoutes());

    return (
        <div className="App">
            <RouterProvider router={router} />
        </div>
    );
};
